'use client';

import { useEffect, useRef } from 'react';

interface PageErrorTrackerProps {
  errorType: '404' | 'error';
  errorMessage?: string;
  errorStack?: string;
}

/**
 * Tracks 404 and error page visits for bug reporting.
 * Sends automatic bug report with referrer info.
 */
export function PageErrorTracker({
  errorType,
  errorMessage,
  errorStack,
}: PageErrorTrackerProps) {
  const reported = useRef(false);

  useEffect(() => {
    // Only report once per mount
    if (reported.current) return;
    reported.current = true;

    const report = async () => {
      try {
        await fetch('/api/bug-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isAutomatic: true,
            severity: errorType === '404' ? 'expected' : 'error',
            errorMessage:
              errorType === '404'
                ? `404 - Page Not Found: ${window.location.pathname}`
                : (errorMessage ?? 'Unknown Error'),
            errorStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language,
            timestampClient: new Date().toISOString(),
            metadata: {
              errorType,
              referrer: document.referrer || null,
              pathname: window.location.pathname,
            },
          }),
        });
      } catch {
        // Silent fail
      }
    };

    void report();
  }, [errorType, errorMessage, errorStack]);

  return null;
}
